import React, { useEffect, useRef, useState } from "react";
import readNDJSONStream from "ndjson-readablestream";
import insubotLogo from "../../assets/Insubot.svg";

import {
    ChatAppRequest,
    ChatAppResponse,
    ChatAppResponseOrError,
    ResponseMessage,
    RetrievalMode,
    useChatMutation,
    useFetchGroups,
    useGenerateSecureContentUrl
} from "@/api";
import { Answer, AnswerError, AnswerLoading } from "@/components/Answer";
import { UserChatMessage } from "@/components/UserChatMessage";
import { PaperAirplaneIcon, TrashIcon } from "@heroicons/react/24/solid";
import { COLLECTION_GROUP_SEPARATOR } from "@/api/util";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { trackEvent, trackException, trackTrace } from "@/appInsights";
import { useStoredTeam } from "@/hooks/useStoredTeam";
import TeamSelector from "@/components/TeamSelector/TeamSelector";

const Chat = () => {
    const { mutateAsync: chatApi } = useChatMutation();

    const { data: contentTeams } = useFetchGroups();
    const [team, setStoredTeam] = useStoredTeam(contentTeams);
    const category = "Default";
    const useSuggestFollowupQuestions = true;
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [answers, setAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [question, setQuestion] = useState<string>("");
    const textAreaDisabled = isLoading || isStreaming || !team;
    const sendQuestionDisabled = isLoading || !question.trim() || !team;
    const selectTeamDisabled = answers.length > 0 || isLoading || isStreaming || contentTeams.length < 2;

    const { mutateAsync: generateSecureContentUrl } = useGenerateSecureContentUrl();

    // eslint-disable-next-line @typescript-eslint/ban-types
    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], _: Function, responseBody: ReadableStream<unknown>) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    const latestResponse: ChatAppResponse = {
                        ...askResponse,
                        choices: [
                            {
                                ...askResponse.choices[0],
                                message: { content: answer, role: askResponse.choices[0].message.role }
                            }
                        ]
                    };
                    setStreamedAnswers([...answers, [question, latestResponse]]);
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["choices"] && event["choices"][0]["context"] && event["choices"][0]["context"]["data_points"]) {
                    event["choices"][0]["message"] = event["choices"][0]["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["choices"] && event["choices"][0]["delta"]["content"]) {
                    setIsLoading(false);
                    await updateState(event["choices"][0]["delta"]["content"]);
                } else if (event["choices"] && event["choices"][0]["context"]) {
                    // Update context with new keys from latest event
                    askResponse.choices[0].context = { ...askResponse.choices[0].context, ...event["choices"][0]["context"] };
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            choices: [
                {
                    ...askResponse.choices[0],
                    message: { content: answer, role: askResponse.choices[0].message.role }
                }
            ]
        };
        return fullResponse;
    };

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);

        // Track chat question event
        trackEvent("ChatQuestionAsked", {
            question,
            selectedTeam: team,
            category: category,
            conversationLength: answers.length
        });

        try {
            const messages: ResponseMessage[] = answers.flatMap(a => [
                { content: a[0], role: "user" },
                { content: a[1].choices[0].message.content, role: "assistant" }
            ]);

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                stream: true,
                context: {
                    overrides: {
                        group: team,
                        collections: [category],
                        top: 3,
                        retrieval_mode: RetrievalMode.Hybrid,
                        semantic_ranker: true,
                        suggest_followup_questions: useSuggestFollowupQuestions
                    }
                },
                // ChatAppProtocol: Client must pass on any session state received from the server
                session_state: answers.length ? answers[answers.length - 1][1].choices[0].session_state : null
            };

            const response = await chatApi(request);
            if (!response.body) {
                setError("No response body");
                return;
            }
            let parsedResponse: ChatAppResponse;
            if (request.stream) {
                parsedResponse = await handleAsyncRequest(question, answers, setAnswers, response.body);
                setAnswers([...answers, [question, parsedResponse]]);
            } else {
                const parsedResponseOrError: ChatAppResponseOrError = await response.json();
                if (response.status > 299 || !response.ok) {
                    setError(parsedResponseOrError.error || "Unknown error");
                    // Track API error
                    trackTrace(`Chat API Error: ${parsedResponseOrError.error || "Unknown error"}`, 3, {
                        statusCode: response.status,
                        selectedTeam: team
                    });
                    return;
                }
                parsedResponse = parsedResponseOrError as ChatAppResponse;
                setAnswers([...answers, [question, parsedResponse]]);
            }

            trackEvent("ChatResponseReceived", {
                responseType: "non-stream",
                question,
                response: parsedResponse,
                selectedTeam: team
            });
        } catch (e) {
            setError(e);
            // Track chat error
            trackException(e as Error, {
                question,
                selectedTeam: team,
                category: category
            });
        } finally {
            setIsLoading(false);
        }
    };

    const sendQuestion = () => {
        if (sendQuestionDisabled) {
            return;
        }

        makeApiRequest(question).then();

        setQuestion("");
    };

    const onEnterPress = (ev: React.KeyboardEvent) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setQuestion(event.currentTarget.value);
    };

    const clearChat = () => {
        // Track chat clear event
        trackEvent("ChatCleared", {
            conversationLength: answers.length,
            selectedTeam: team
        });

        lastQuestionRef.current = "";
        error && setError(undefined);
        setAnswers([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
    };

    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" }), [isLoading]);
    useEffect(() => chatMessageStreamEnd.current?.scrollIntoView({ behavior: "auto" }), [streamedAnswers]);

    const onShowCitation = (citation: string) => {
        generateSecureContentUrl({
            path: citation,
            collection: `${category}${COLLECTION_GROUP_SEPARATOR}${team}`
        }).then(({ url }) => {
            console.log("Opening URL: ", url);
            window.open(url, "_blank");
        });
    };

    return (
        <div className="mx-auto my-4 flex max-w-4xl grow select-none flex-col px-2">
            <div className="mb-6 flex justify-center px-2">
                <img src={insubotLogo} className="h-16 w-auto" alt="Insubot logo" />
            </div>
            <div className="flex grow flex-col items-center">
                <div className="w-full grow flex-col">
                    {lastQuestionRef.current && (
                        <div className={"flex w-full grow select-text flex-col gap-4"}>
                            {isStreaming &&
                                streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index} className={"flex flex-col gap-4"}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={"flex max-w-[80%]"}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                isSelected={false}
                                                onCitationClicked={c => onShowCitation(c)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!isStreaming &&
                                answers.map((answer, index) => (
                                    <div key={index} className={"flex flex-col gap-4"}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={"flex max-w-[80%]"}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answer[1]}
                                                onCitationClicked={c => onShowCitation(c)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers.length - 1 === index}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {isLoading && (
                                <div className={"flex flex-col gap-4"}>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={"max-w-[500px]"}>
                                        <AnswerLoading />
                                    </div>
                                </div>
                            )}
                            {error ? (
                                <div className={"flex flex-col gap-4"}>
                                    <UserChatMessage message={lastQuestionRef.current} />
                                    <div className={"max-w-[500px]"}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </div>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className="mt-auto w-full">
                        <Card>
                            <CardContent className="pt-4">
                                <form>
                                    <div className="grid gap-6">
                                        {/* Team Selection */}
                                        <TeamSelector teams={contentTeams} selectedTeam={team} onTeamChange={setStoredTeam} disabled={selectTeamDisabled} />

                                        {/* Question Input */}
                                        <div className="flex flex-col">
                                            <Label htmlFor="question">Ask a question</Label>
                                            <Textarea
                                                id="question"
                                                disabled={textAreaDisabled}
                                                className="mt-1"
                                                rows={3}
                                                value={question}
                                                onChange={onQuestionChange}
                                                onKeyDown={onEnterPress}
                                                placeholder="What would you like to know about your uploaded files?"
                                            />
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                            <CardFooter className="flex justify-between">
                                <Button type="button" disabled={sendQuestionDisabled} onClick={sendQuestion}>
                                    <PaperAirplaneIcon className="-ml-1 mr-2 size-5" aria-hidden="true" />
                                    Ask Question
                                </Button>
                                {lastQuestionRef.current && (
                                    <Button onClick={clearChat} variant="destructive">
                                        <TrashIcon className="-ml-1 mr-2 size-5" aria-hidden="true" />
                                        Clear Chat
                                    </Button>
                                )}
                            </CardFooter>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Chat;
